import React from 'react'
import styled from 'styled-components'

// @ts-expect-error: no types in this version of @gnomad/ui
import { ExternalLink, PageHeading, Tabs } from '@gnomad/ui'

import DocumentTitle from '../DocumentTitle'
import Fetch from '../Fetch'
import HelpButton from '../HelpButton'
import StatusMessage from '../StatusMessage'
import RegionViewer from './AutosizedRegionViewer'
import { ExacConstraintTable, GnomadConstraintTable } from './Constraint'
import GeneInfo from './GeneInfo'
import GeneResults from './GeneResults'
import { TrackPage, TrackPageSection } from './TrackPage'
import TranscriptTrack from './TranscriptTrack'
import VariantsInGene from './VariantsInGene'
import ClinVarVariantsInGene from './ClinVarVariantsInGene'
import {
  DatasetId,
  RenderVariantAttributes,
  VariantColumnConfig,
  VariantConsequence,
  VariantConsequenceCategoryLabels,
  VariantCustomFilter,
  VariantLollipopTrackGroup,
} from '../Browser'
import { DEFAULT_VARIANT_CATEGORY_OPTIONS, VariantCategoryOption } from '../variantCategories'
import { IndividualGeneAPIResponse } from '../GeneResultsPage/geneResultTableColumns'
import { SortOrder } from './VariantTable'
import { VariantTableColumn } from './variantTableColumns'

const GeneName = styled.span`
  font-size: 0.75em;
  font-weight: 400;
`

const TablesWrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  margin-bottom: 3em;
`

const ConstraintWrapper = styled.div`
  @media (min-width: 700px) {
    min-width: 415px;
  }
`

interface GenePageProps {
  datasetId: DatasetId
  browserTitle: string
  gene: IndividualGeneAPIResponse
  showManeSelectTranscript?: boolean
  defaultVariantAnalysisGroup: string
  variantAnalysisGroupLabels?: Record<string, string>
  variantAnalysisGroupOptions: readonly string[]
  variantSortKey?: string
  variantSortOrder?: SortOrder
  variantConsequences: VariantConsequence[]
  variantConsequenceCategoryLabels?: VariantConsequenceCategoryLabels
  variantCategoryOptions?: VariantCategoryOption[]
  getVariantCategory?: (variant: any) => string
  variantResultColumns: VariantColumnConfig[]
  variantCustomFilter?: VariantCustomFilter
  renderVariantAttributes?: RenderVariantAttributes
  additionalVariantDetailSummaryColumns?: VariantTableColumn[]
  variantDetailColumns?: VariantTableColumn[]
  variantDetailsSingleAnalysisGroup?: boolean
  renderVariantTranscriptConsequences?: boolean
  variantAlleleFrequencyOverride?: number
  variantExportNote?: string
  variantLollipopTrackGroups?: VariantLollipopTrackGroup[]
}

const GenePage = ({
  datasetId,
  browserTitle,
  gene,
  showManeSelectTranscript = false,
  defaultVariantAnalysisGroup,
  variantAnalysisGroupLabels = undefined,
  variantAnalysisGroupOptions,
  variantSortKey = 'variant_id',
  variantSortOrder = 'ascending',
  variantConsequences,
  variantConsequenceCategoryLabels = undefined,
  variantCategoryOptions = DEFAULT_VARIANT_CATEGORY_OPTIONS,
  getVariantCategory = undefined,
  variantResultColumns,
  variantCustomFilter = undefined,
  additionalVariantDetailSummaryColumns = undefined,
  renderVariantAttributes = undefined,
  variantDetailColumns = undefined,
  variantDetailsSingleAnalysisGroup = false,
  renderVariantTranscriptConsequences = false,
  variantAlleleFrequencyOverride = undefined,
  variantExportNote = undefined,
  variantLollipopTrackGroups = undefined,
}: GenePageProps) => {
  return (
    <TrackPage>
      <TrackPageSection>
        <DocumentTitle title={`${gene.symbol} | ${browserTitle}`} />
        <PageHeading>
          {gene.symbol} <GeneName>{gene.name}</GeneName>
        </PageHeading>

        <GeneInfo gene={gene} showManeSelectTranscript={showManeSelectTranscript} />

        <TablesWrapper>
          <div>
            <GeneResults gene={gene} geneResults={gene.gene_results} />
          </div>
          <ConstraintWrapper>
            <h2>
              Constraint{' '}
              <HelpButton
                popupTitle="Constraint"
                popupContent={
                  <p>
                    Metrics for quantification intolerance to loss-of-function / protein-truncating
                    variation as calculated by the gnomAD consortium. For more information, please
                    visit the{' '}
                    <ExternalLink href="https://gnomad.broadinstitute.org">
                      gnomAD browser
                    </ExternalLink>
                    , and read our{' '}
                    <ExternalLink href="https://gnomad.broadinstitute.org/help/constraint">
                      help text
                    </ExternalLink>
                    . Please note that insertions and deletions are excluded in the aggregated
                    counts and calculated metrics.
                  </p>
                }
              />
            </h2>
            <Tabs
              tabs={[
                {
                  id: 'gnomad_v4',
                  label: 'gnomAD v4',
                  render: () =>
                    gene.gnomad_v4_constraint ? (
                      <GnomadConstraintTable constraint={gene.gnomad_v4_constraint} />
                    ) : (
                      <p>gnomAD v4 constraint is not available for this gene.</p>
                    ),
                },
                {
                  id: 'gnomad_v2',
                  label: 'gnomAD v2',
                  render: () =>
                    gene.gnomad_v2_constraint ? (
                      <GnomadConstraintTable constraint={gene.gnomad_v2_constraint} />
                    ) : (
                      <p>gnomAD v2 constraint is not available for this gene.</p>
                    ),
                },
                {
                  id: 'exac',
                  label: 'ExAC',
                  render: () =>
                    gene.exac_constraint ? (
                      <ExacConstraintTable constraint={gene.exac_constraint} />
                    ) : (
                      <p>ExAC constraint is not available for this gene.</p>
                    ),
                },
              ].filter((tab) => datasetId !== 'AGB' || tab.id !== 'exac')}
            />
          </ConstraintWrapper>
        </TablesWrapper>
      </TrackPageSection>
      <RegionViewer padding={75} regions={gene.canonical_transcript.exons}>
        <TranscriptTrack
          exons={gene.canonical_transcript.exons}
          strand={gene.canonical_transcript.strand}
        />
        <ClinVarVariantsInGene
          datasetId={datasetId}
          gene={gene}
          variantConsequences={variantConsequences}
          consequenceCategoryLabels={variantConsequenceCategoryLabels}
        />
        <VariantsInGene
          datasetId={datasetId}
          defaultVariantAnalysisGroup={defaultVariantAnalysisGroup}
          gene={gene}
          renderVariantAttributes={renderVariantAttributes}
          additionalVariantDetailSummaryColumns={additionalVariantDetailSummaryColumns}
          variantDetailColumns={variantDetailColumns}
          variantDetailsSingleAnalysisGroup={variantDetailsSingleAnalysisGroup}
          renderVariantTranscriptConsequences={renderVariantTranscriptConsequences}
          variantAnalysisGroupLabels={variantAnalysisGroupLabels}
          variantAnalysisGroupOptions={variantAnalysisGroupOptions}
          variantSortKey={variantSortKey}
          variantSortOrder={variantSortOrder}
          variantConsequences={variantConsequences}
          variantCategoryOptions={variantCategoryOptions}
          getVariantCategory={getVariantCategory}
          variantCustomFilter={variantCustomFilter}
          variantResultColumns={variantResultColumns}
          variantAlleleFrequencyOverride={variantAlleleFrequencyOverride}
          variantExportNote={variantExportNote}
          variantLollipopTrackGroups={variantLollipopTrackGroups}
        />
      </RegionViewer>
    </TrackPage>
  )
}

interface GenePageContainerProps {
  datasetId: DatasetId
  geneIdOrSymbol: string
  showManeSelectTranscript?: boolean
  browserTitle: string
  defaultVariantAnalysisGroup: string
  variantAnalysisGroupOptions: readonly string[]
  variantConsequences: VariantConsequence[]
  variantResultColumns: VariantColumnConfig[]
  [key: string]: any
}

// TK: TODO: fixme: get rid of the ...otherProps,
// I personally don't like this pattern, it hides props from the reader
const GenePageContainer = ({
  datasetId,
  geneIdOrSymbol,
  showManeSelectTranscript = false,
  defaultVariantAnalysisGroup,
  variantAnalysisGroupOptions,
  variantConsequences,
  variantResultColumns,
  ...otherProps
}: GenePageContainerProps) => {
  return (
    <Fetch path={`/gene/${geneIdOrSymbol}`}>
      {({
        data: geneData,
        error: geneError,
        loading: geneLoading,
      }: {
        data: { gene: IndividualGeneAPIResponse }
        error: Error | null
        loading: boolean
      }) => {
        if (geneLoading) {
          return <StatusMessage>Loading gene...</StatusMessage>
        }

        if (geneError || !(geneData || {}).gene) {
          return <StatusMessage>{geneError?.message || 'Unable to load gene'}</StatusMessage>
        }

        return (
          <GenePage
            datasetId={datasetId}
            gene={geneData.gene}
            showManeSelectTranscript={showManeSelectTranscript}
            defaultVariantAnalysisGroup={defaultVariantAnalysisGroup}
            variantAnalysisGroupOptions={variantAnalysisGroupOptions}
            variantConsequences={variantConsequences}
            variantResultColumns={variantResultColumns}
            {...otherProps}
          />
        )
      }}
    </Fetch>
  )
}

export default GenePageContainer
